import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { accountService } from '../../services/api/accountService';
import { authService } from '../../services/api/authService';
import { useAuth } from '../../context/AuthContext';
import LoadingOverlay from '../common/LoadingOverlay';
import AuthCard from '../common/AuthCard/AuthCard';
import {
  Field,
  FieldLabel,
  Form,
  PasswordWrap,
  RuleItem,
  RuleList,
  StatusMessage,
  SubmitButton,
  TextInput,
  TextLink,
} from '../common/AuthCard/AuthCard.styled';

const REQUIREMENT_ROWS = [
  { key: 'length', text: 'At least 8 characters' },
  { key: 'uppercase', text: 'One uppercase letter' },
  { key: 'lowercase', text: 'One lowercase letter' },
  { key: 'number', text: 'One number' },
  { key: 'specialChar', text: 'One special character' },
  { key: 'passwordsMatch', text: 'Passwords match' },
];

export const ChangePasswordForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [shown, setShown] = useState({ current: false, new: false, confirm: false });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const v = formData.newPassword;
  const newValidation = {
    length: v.length >= 8,
    uppercase: /[A-Z]/.test(v),
    lowercase: /[a-z]/.test(v),
    number: /\d/.test(v),
    specialChar: /[!@#$%^&*()_\-+={[}\]|;:,<.>/?]/.test(v),
    passwordsMatch: v === formData.confirmPassword && v !== '',
  };

  const isFormValid =
    formData.currentPassword.length > 0 && Object.values(newValidation).every(Boolean);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    setError('');
    setIsSubmitting(true);

    try {
      await accountService.changePassword(formData.currentPassword, formData.newPassword);
      await authService.logOut();
      await authService.login(user.Email, formData.newPassword);
      alert('Password changed successfully');
      navigate('/');
    } catch (err) {
      console.error('Change password error:', err);
      const match = /status:\s*(\d+)/.exec(err.message);
      const status = match ? parseInt(match[1], 10) : null;
      switch (status) {
        case 401:
          setError('Your current password is incorrect. Please try again or reset your password.');
          break;
        case 404:
          setError('Your account was not found. Please contact support.');
          break;
        case 409:
          setError('The new password can’t be the same as any password you’ve used before.');
          break;
        default:
          setError(err.message || 'An unexpected error occurred. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordField = ({ name, field, label, autoComplete }) => (
    <Field>
      <FieldLabel htmlFor={`change-password-${field}`}>{label}</FieldLabel>
      <PasswordWrap>
        <TextInput
          id={`change-password-${field}`}
          name={name}
          type={shown[field] ? 'text' : 'password'}
          value={formData[name]}
          onChange={handleChange}
          autoComplete={autoComplete}
          required
          disabled={isSubmitting}
        />
        <IconButton
          size="small"
          onClick={() => setShown((prev) => ({ ...prev, [field]: !prev[field] }))}
          aria-label={`${shown[field] ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
        >
          {shown[field] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </PasswordWrap>
    </Field>
  );

  return (
    <>
      <AuthCard
        title="Change your password"
        lead={user ? `Signed in as ${user.FirstName} ${user.LastName}` : undefined}
        footer={
          <p>
            Forgot your current password?{' '}
            <TextLink to="/forgot-password">Reset it by email</TextLink>
          </p>
        }
      >
        <Form onSubmit={handleSubmit} noValidate>
          {passwordField({
            name: 'currentPassword',
            field: 'current',
            label: 'Current password',
            autoComplete: 'current-password',
          })}
          {passwordField({
            name: 'newPassword',
            field: 'new',
            label: 'New password',
            autoComplete: 'new-password',
          })}
          {passwordField({
            name: 'confirmPassword',
            field: 'confirm',
            label: 'Confirm new password',
            autoComplete: 'new-password',
          })}

          <RuleList aria-label="New password requirements">
            {REQUIREMENT_ROWS.map(({ key, text }) => (
              <RuleItem key={key} $met={newValidation[key]}>
                {newValidation[key] ? (
                  <CheckCircleRoundedIcon aria-hidden />
                ) : (
                  <RadioButtonUncheckedRoundedIcon aria-hidden />
                )}
                {text}
              </RuleItem>
            ))}
          </RuleList>

          {error && (
            <StatusMessage $variant="error" role="alert">
              {error}
            </StatusMessage>
          )}

          <SubmitButton type="submit" disabled={isSubmitting || !isFormValid}>
            {isSubmitting ? 'Saving…' : 'Save password'}
          </SubmitButton>
        </Form>
      </AuthCard>
      {isSubmitting && <LoadingOverlay />}
    </>
  );
};

export default ChangePasswordForm;
